import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import { Coins, Gift, X } from 'components/Icons';
import { useRemScale } from 'lib/remScale';
import { logEvent } from 'utils/firebase/analytics';
import {
  REWARDS_STORE_OPENED,
  REWARD_REDEEM_CLICKED,
  REWARD_REDEEM_CONFIRMED,
  REWARD_REDEEM_RESULT,
} from 'utils/firebase/analytics.config';
import { requestReward } from 'utils/quikcoinsService';
import { CSV_REWARDS } from './rewardsCatalog';

const REWARDS = [
  {
    id: 'robux-1000',
    name: '1,000 Robux',
    cost: 500,
    description: 'Get 1,000 Robux for your Roblox account. Digital code delivered via email.',
    image: 'https://livecards.net/pl/74395.jpg',
    bgColor: 'from-gray-700 to-gray-900',
  },
  // ...CSV_REWARDS,
];

export function RewardsStore({
  quikcoins = 0,
  open,
  onOpenChange,
  hideTrigger = false,
  userId,
  authToken,
  onBalanceRefresh,
}) {
  const { rem } = useRemScale();
  const { width, height } = useWindowDimensions();
  const storeUserId = useSelector(state => state.app?.courseTokenValues?.userId);
  const storeToken = useSelector(state => state.meeting?.properties?.token);
  const curriculumGS = useSelector(state => state.course?.curriculumGradeAndSubject);
  const courseData = useSelector(state => state.course?.courseData);
  const unitId = useSelector(state => state.app?.courseTokenValues?.unitId);
  const worldToLoad = useSelector(state => state.app?.courseTokenValues?.worldToLoad);
  const tutor = useSelector(state => state.app?.courseTokenValues?.AITeacherToLoad);
  const [internalOpen, setInternalOpen] = useState(false);
  const [redeemingRewardId, setRedeemingRewardId] = useState(null);
  const [dialog, setDialog] = useState({
    visible: false,
    title: '',
    message: '',
    buttons: [],
  });

  const resolvedUserId = userId || storeUserId;
  const resolvedToken = authToken || storeToken;

  const unitTitle = useMemo(() => {
    try {
      const units = courseData?.units || [];
      if (!units.length || !unitId) return undefined;
      const match =
        units.find(
          u =>
            (u?.id || u?.unitId || u?.unitID || u?.unit_id) === unitId,
        ) || null;
      return match?.title || match?.qlUnit || undefined;
    } catch (_) {
      return undefined;
    }
  }, [courseData, unitId]);

  const baseAnalyticsProps = useMemo(
    () => ({
      user_id: resolvedUserId ?? null,
      grade: curriculumGS?.grade || undefined,
      subject: curriculumGS?.subject || undefined,
      unit_id: unitId || undefined,
      unit_title: unitTitle || undefined,
      world_to_load: worldToLoad || undefined,
      tutor: tutor || undefined,
    }),
    [
      curriculumGS?.grade,
      curriculumGS?.subject,
      resolvedUserId,
      unitId,
      unitTitle,
      worldToLoad,
      tutor,
    ],
  );

  const trackEventSafely = useCallback((eventName, params) => {
    try {
      const result = logEvent(eventName, params);
      if (result && typeof result.catch === 'function') {
        result.catch((error) => {
          console.warn('[RewardsStore] logEvent failed', eventName, error);
        });
      }
    } catch (error) {
      console.warn('[RewardsStore] logEvent failed', eventName, error);
    }
  }, []);

  const showAlert = useCallback((title, message, buttons = []) => {
    const normalizedButtons =
      Array.isArray(buttons) && buttons.length ? buttons : [{ text: 'OK' }];
    setDialog({
      visible: true,
      title: title || '',
      message: message || '',
      buttons: normalizedButtons,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(prev => ({ ...prev, visible: false }));
  }, []);

  const handleDialogPress = useCallback(
    (button) => {
      closeDialog();
      if (button?.onPress) {
        button.onPress();
      }
    },
    [closeDialog],
  );

  const handleDialogBackdropPress = useCallback(() => {
    const cancelButton =
      dialog.buttons?.find((button) => button?.style === 'cancel') ||
      dialog.buttons?.[0];
    if (cancelButton) {
      handleDialogPress(cancelButton);
    } else {
      closeDialog();
    }
  }, [closeDialog, dialog.buttons, handleDialogPress]);

  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = (value) => {
    if (isControlled) {
      if (onOpenChange) onOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };

  const rewardsModalWidth = Math.min(rem(780), width - rem(24) * 2);
  const dialogModalWidth = Math.min(rem(360), width - rem(24) * 2);
  const modalMaxHeight = Math.round(height * 0.9);
  const rewardColumns = 3;
  const rewardGap = rem(12);
  const rewardsModalPadding = rem(16);
  // Reserve a small gutter for scrollbars/rounding so the third card does not wrap.
  const rewardGridFitOffset = rem(14);
  const rewardCardWidth = Math.floor(
    (rewardsModalWidth -
      rewardsModalPadding * 2 -
      rewardGap * (rewardColumns - 1) -
      rewardGridFitOffset) /
      rewardColumns,
  );
  const rewardImageHeight = Math.max(72, Math.round(rewardCardWidth * 0.72));
  const rewardCompactLayout = rewardCardWidth < rem(140);
  const rewardTitleLineHeight = rewardCompactLayout ? rem(15) : rem(18);
  const rewardDescriptionLineHeight = rewardCompactLayout ? rem(14) : rem(16);
  const rewardTitleMinHeight = rewardCompactLayout ? rem(30) : rem(36);
  const rewardDescriptionMinHeight = rewardCompactLayout ? rem(28) : rem(32);
  const rewardItemsInLastRow = REWARDS.length % rewardColumns || rewardColumns;
  const rewardLastRowStartIndex = REWARDS.length - rewardItemsInLastRow;

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    if (!wasOpen && isOpen) {
      trackEventSafely(REWARDS_STORE_OPENED, {
        ...baseAnalyticsProps,
        balance:
          typeof quikcoins === 'number' && Number.isFinite(quikcoins)
            ? Math.max(0, quikcoins)
            : undefined,
        source: 'rewards_store_modal',
      });
      if (onBalanceRefresh) {
        onBalanceRefresh();
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [
    baseAnalyticsProps,
    isOpen,
    onBalanceRefresh,
    quikcoins,
    trackEventSafely,
  ]);

  const handleRedeemConfirm = async (reward) => {
    if (!resolvedUserId) {
      showAlert('Unable to redeem', 'User information is missing. Please try again later.');
      return;
    }
    if (redeemingRewardId) return;
    const rewardCost =
      typeof reward?.cost === 'number' && Number.isFinite(reward.cost)
        ? Math.max(0, reward.cost)
        : undefined;
    const balanceBefore =
      typeof quikcoins === 'number' && Number.isFinite(quikcoins)
        ? Math.max(0, quikcoins)
        : undefined;
    trackEventSafely(REWARD_REDEEM_CONFIRMED, {
      ...baseAnalyticsProps,
      balance_before: balanceBefore,
      reward_id: reward?.id || undefined,
      reward_name: reward?.name || undefined,
      reward_cost: rewardCost,
      source: 'rewards_store_modal',
    });
    setRedeemingRewardId(reward.id);
    try {
      const result = await requestReward({
        reward,
        userId: resolvedUserId,
        token: resolvedToken,
      });
      if (result?.success) {
        const balanceAfter =
          typeof balanceBefore === 'number' && typeof rewardCost === 'number'
            ? Math.max(0, balanceBefore - rewardCost)
            : undefined;
        trackEventSafely(REWARD_REDEEM_RESULT, {
          ...baseAnalyticsProps,
          balance_before: balanceBefore,
          balance_after: balanceAfter,
          reward_id: reward?.id || undefined,
          reward_name: reward?.name || undefined,
          reward_cost: rewardCost,
          success: true,
          source: 'rewards_store_modal',
        });
        if (onBalanceRefresh) {
          await onBalanceRefresh();
        }
        showAlert(
          'Congratulations!',
          'Your reward has been processed successfully! You will receive it via email soon.',
          [{ text: 'OK', onPress: () => setOpen(false) }],
        );
      } else {
        trackEventSafely(REWARD_REDEEM_RESULT, {
          ...baseAnalyticsProps,
          balance_before: balanceBefore,
          reward_id: reward?.id || undefined,
          reward_name: reward?.name || undefined,
          reward_cost: rewardCost,
          success: false,
          error_message: result?.error || undefined,
          source: 'rewards_store_modal',
        });
        showAlert(
          'Redemption Failed',
          result?.error || 'An unexpected error occurred while redeeming your reward.',
        );
      }
    } catch (error) {
      trackEventSafely(REWARD_REDEEM_RESULT, {
        ...baseAnalyticsProps,
        balance_before: balanceBefore,
        reward_id: reward?.id || undefined,
        reward_name: reward?.name || undefined,
        reward_cost: rewardCost,
        success: false,
        error_message: error?.message || undefined,
        source: 'rewards_store_modal',
      });
      showAlert(
        'Redemption Failed',
        error?.message || 'An unexpected error occurred while redeeming your reward.',
      );
    } finally {
      setRedeemingRewardId(null);
    }
  };

  const handleRewardSelect = (reward) => {
    const rewardCost =
      typeof reward?.cost === 'number' && Number.isFinite(reward.cost)
        ? Math.max(0, reward.cost)
        : undefined;
    const canUserRedeem = typeof rewardCost === 'number' ? quikcoins >= rewardCost : false;
    trackEventSafely(REWARD_REDEEM_CLICKED, {
      ...baseAnalyticsProps,
      balance:
        typeof quikcoins === 'number' && Number.isFinite(quikcoins)
          ? Math.max(0, quikcoins)
          : undefined,
      reward_id: reward?.id || undefined,
      reward_name: reward?.name || undefined,
      reward_cost: rewardCost,
      can_user_redeem: canUserRedeem,
      source: 'rewards_store_modal',
    });
    if (!canUserRedeem) {
      showAlert('Insufficient Quikcoins', "You don't have enough Quikcoins for this reward!");
      return;
    }

    showAlert(
      'Confirm Redemption',
      `Are you sure you want to redeem ${reward.name} for ${reward.cost} Quikcoins?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Redeem',
          onPress: () => handleRedeemConfirm(reward),
        },
      ],
    );
  };

  return (
    <>
      {!hideTrigger && (
        <TouchableOpacity
          onPress={() => setOpen(true)}
          activeOpacity={0.9}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#C5E836',
            paddingHorizontal: rem(12),
            paddingVertical: rem(8),
            borderRadius: rem(8),
          }}
        >
          <Gift size={rem(16)} color="#000" strokeWidth={2.5} />
          <Text
            style={{
              marginLeft: rem(6),
              color: '#000',
              fontSize: rem(14),
              fontWeight: '700',
            }}
          >
            Rewards Store
          </Text>
        </TouchableOpacity>
      )}

      <Modal
        visible={isOpen}
        animationType="fade"
        transparent={true}
        onRequestClose={() => setOpen(false)}
        supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}
      >
        <View
          style={[StyleSheet.absoluteFillObject, { justifyContent: 'center', alignItems: 'center' }]}
          pointerEvents="box-none"
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={() => setOpen(false)}
          />

          <View
            style={{
              width: rewardsModalWidth,
              maxHeight: modalMaxHeight,
              backgroundColor: 'rgba(0,0,0,0.95)',
              borderRadius: rem(8),
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.10)',
              padding: rem(16),
            }}
          >
            <View style={{ marginBottom: rem(16) }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: rem(4),
                }}
              >
                <Text
                  style={{
                    color: '#fff',
                    fontSize: rem(24),
                    fontWeight: '700',
                  }}
                >
                  Rewards Store
                </Text>
                <TouchableOpacity
                  onPress={() => setOpen(false)}
                  style={{
                    padding: rem(8),
                    margin: rem(-8),
                    opacity: 0.8,
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <X size={rem(16)} color="#fff" strokeWidth={2.5} />
                </TouchableOpacity>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: rem(2),
                }}
              >
                <Text
                  style={{
                    color: 'rgba(255,255,255,0.60)',
                    fontSize: rem(14),
                  }}
                >
                  Your balance:
                </Text>
                <View
                  style={{
                    marginLeft: rem(8),
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Coins size={rem(14)} color="#C5E836" strokeWidth={2.5} />
                  <Text
                    style={{
                      marginLeft: rem(6),
                      color: '#C5E836',
                      fontSize: rem(14),
                      fontWeight: '700',
                    }}
                  >
                    {typeof quikcoins === 'number' && Number.isFinite(quikcoins)
                      ? Math.max(0, quikcoins)
                      : 0}
                  </Text>
                </View>
              </View>
            </View>

            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingBottom: rem(16) }}
              style={{ maxHeight: modalMaxHeight - rem(100) }}
            >
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {REWARDS.map((reward, index) => {
                  const isRedeeming = redeemingRewardId === reward.id;
                  const isLastColumn = (index + 1) % rewardColumns === 0;
                  const isInLastRow = index >= rewardLastRowStartIndex;
                  return (
                    <View
                      key={reward.id}
                      style={{
                        width: rewardCardWidth,
                        backgroundColor: '#18181b',
                        borderRadius: rem(12),
                        overflow: 'hidden',
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.10)',
                        marginRight: isLastColumn ? 0 : rewardGap,
                        marginBottom: isInLastRow ? 0 : rewardGap,
                      }}
                    >
                      <View
                        style={{
                          height: rewardImageHeight,
                          backgroundColor: '#ffffff',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: rem(8),
                        }}
                      >
                        <Image
                          source={{ uri: reward.image }}
                          style={{ width: '90%', height: '90%' }}
                          resizeMode="contain"
                        />
                      </View>

                      <View style={{ padding: rewardCompactLayout ? rem(8) : rem(12) }}>
                        <Text
                          numberOfLines={2}
                          style={{
                            color: '#fff',
                            fontSize: rewardCompactLayout ? rem(12) : rem(14),
                            lineHeight: rewardTitleLineHeight,
                            minHeight: rewardTitleMinHeight,
                            fontWeight: '700',
                            marginBottom: rewardCompactLayout ? rem(6) : rem(8),
                          }}
                        >
                          {reward.name}
                        </Text>

                        <Text
                          numberOfLines={2}
                          style={{
                            color: 'rgba(255,255,255,0.65)',
                            fontSize: rewardCompactLayout ? rem(11) : rem(12),
                            lineHeight: rewardDescriptionLineHeight,
                            minHeight: rewardDescriptionMinHeight,
                            marginBottom: rewardCompactLayout ? rem(8) : rem(10),
                          }}
                        >
                          {reward.description}
                        </Text>

                        <View
                          style={{
                            flexDirection: rewardCompactLayout ? 'column' : 'row',
                            alignItems: rewardCompactLayout ? 'flex-start' : 'center',
                            justifyContent: rewardCompactLayout ? 'flex-start' : 'space-between',
                            width: '100%',
                          }}
                        >
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              flex: rewardCompactLayout ? 0 : 1,
                              minWidth: 0,
                              marginRight: rewardCompactLayout ? 0 : rem(6),
                            }}
                          >
                            <Coins
                              size={rewardCompactLayout ? rem(14) : rem(16)}
                              color="#C5E836"
                              strokeWidth={2.5}
                            />
                            <Text
                              style={{
                                marginLeft: rem(6),
                                color: '#C5E836',
                                fontSize: rewardCompactLayout ? rem(12) : rem(12),
                                fontWeight: '700',
                                flexShrink: 1,
                              }}
                            >
                              {reward.cost} Quikcoins
                            </Text>
                          </View>

                          <TouchableOpacity
                            onPress={() => handleRewardSelect(reward)}
                            activeOpacity={0.9}
                            disabled={isRedeeming}
                            style={{
                              marginTop: rewardCompactLayout ? rem(8) : 0,
                              backgroundColor: '#C5E836',
                              paddingHorizontal: rewardCompactLayout ? rem(12) : rem(12),
                              paddingVertical: rewardCompactLayout ? rem(6) : rem(8),
                              borderRadius: rem(8),
                              opacity: isRedeeming ? 0.7 : 1,
                              minWidth: rewardCompactLayout ? rem(74) : rem(74),
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Text
                              style={{
                                color: '#000',
                                fontSize: rewardCompactLayout ? rem(12) : rem(14),
                                fontWeight: '700',
                              }}
                            >
                              {isRedeeming ? 'Redeeming...' : 'Redeem'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                  </View>
                  );
                })}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        visible={dialog.visible}
        animationType="fade"
        transparent={true}
        onRequestClose={closeDialog}
        supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}
      >
        <View
          style={[StyleSheet.absoluteFillObject, { justifyContent: 'center', alignItems: 'center' }]}
          pointerEvents="box-none"
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={handleDialogBackdropPress}
          />

          <View
            style={{
              width: dialogModalWidth,
              maxWidth: dialogModalWidth,
              backgroundColor: 'rgba(0,0,0,0.96)',
              borderRadius: rem(10),
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.12)',
              padding: rem(16),
            }}
          >
            <Text
              style={{
                color: '#fff',
                fontSize: rem(18),
                fontWeight: '700',
                marginBottom: dialog.message ? rem(8) : 0,
              }}
            >
              {dialog.title}
            </Text>

            {dialog.message ? (
              <Text
                style={{
                  color: 'rgba(255,255,255,0.70)',
                  fontSize: rem(14),
                  marginBottom: rem(16),
                }}
              >
                {dialog.message}
              </Text>
            ) : null}

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {(dialog.buttons || []).map((button, index) => {
                const isCancel = button?.style === 'cancel';
                return (
                  <TouchableOpacity
                    key={`${button?.text || 'button'}-${index}`}
                    onPress={() => handleDialogPress(button)}
                    activeOpacity={0.9}
                    style={{
                      backgroundColor: isCancel ? 'rgba(255,255,255,0.12)' : '#C5E836',
                      paddingHorizontal: rem(16),
                      paddingVertical: rem(10),
                      borderRadius: rem(8),
                      marginLeft: index === 0 ? 0 : rem(8),
                      minWidth: rem(88),
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      style={{
                        color: isCancel ? '#fff' : '#000',
                        fontSize: rem(14),
                        fontWeight: '700',
                      }}
                    >
                      {button?.text || 'OK'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

export default RewardsStore;
